import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { REQUIRED_FIELDS_MISSING_MESSAGE } from '@constants';
import { Address } from '@models/address.model';
import { User } from '@models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { AutocompleteSuggestion, GoogleService } from '@services/google.service';
import { UserService } from '@services/user.service';
import { extractComponentsFromGooglePlace } from '@utilities/google-maps-utilities';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'gc-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  standalone: false,
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() editingUserAccount = false;
  @Input() addressType: string;
  @Input() alternativeAddress: Address;
  @Input() userType: string;
  @Input() addressForm: FormGroup;
  @ViewChild('locationInput', { static: false }) locationInput: ElementRef<HTMLInputElement>;
  address$: Observable<Address>;
  unsubscribe$ = new Subject<void>();
  user: User;
  addressSuggestions: AutocompleteSuggestion[] = [];
  private readonly googleService: GoogleService = inject(GoogleService);
  constructor(
    private readonly alertService: AlertService,
    private readonly cdr: ChangeDetectorRef,
    private readonly userService: UserService,
    private readonly configService: ConfigLocaleService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit() {
    this.address$ = this.addressForm.get('address').valueChanges.pipe(map((a) => ({ ...a, type: this.addressType })));

    this.userService.profile$
      .pipe(
        filter((user) => !!user),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((user) => {
        this.user = user;
      });
  }

  async populateAddressFields(event: Event) {
    this.addressSuggestions = await this.googleService.allAddressSugestions(
      (event.target as HTMLInputElement).value,
      this.configService.$addressAutoComplete(),
    );
  }

  async onOptionSelected(place: AutocompleteSuggestion) {
    const googlePlace = (await this.googleService.getPlace(place)).addressComponents;
    const { line1, suburb, line2: city, code: postcode } = extractComponentsFromGooglePlace(googlePlace);
    this.addressForm.get('address').patchValue({ line1, suburb, city, postcode, type: this.addressType });
    this.addressForm.get('address').markAsDirty();
    this.cdr.detectChanges();
  }

  onSubmit() {
    if (this.addressForm.get('address').valid) {
      const address = this.addressForm.get('address').value;
      const addressToUpdate =
        this.addressType === 'physical'
          ? { physical_address: { ...address, type: this.addressType } }
          : { postal_address: { ...address, type: this.addressType } };

      this.userService.updateUserProfile(addressToUpdate).subscribe(
        () => {
          const addressKey = this.addressType === 'physical' ? 'address_updated_physical' : 'address_updated_postal';
          this.alertService.snackbar(addressKey);
          this.addressForm.get('address').markAsPristine();
        },
        (err: unknown) => {
          this.alertService.handleErrorDialog$(err, this.translate.instant('error_updating_address'));
        },
      );
    } else {
      this.alertService.snackbar(REQUIRED_FIELDS_MISSING_MESSAGE);
      this.addressForm.markAllAsTouched();
    }
  }

  copyAddress() {
    if (this.alternativeAddress) {
      this.addressForm.get('address').patchValue(this.alternativeAddress);
    } else {
      if (this.user.physicalAddress && this.addressType === 'physical') {
        this.addressForm.get('address').patchValue(this.user.physicalAddress);
      }
      if (this.user.postalAddress && this.addressType === 'postal') {
        this.addressForm.get('address').patchValue(this.user.postalAddress);
      }
    }
    this.addressForm.get('address').markAsDirty();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }
}
